import React, { useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import {
  Search,
  ChevronDown,
  MessageCircle,
  Phone,
  ArrowRight,
  Sparkles,
} from 'lucide-react';
import {
  FAQ_CATEGORIES,
  flattenFaqForSchema,
  allFaqSearchBlob,
  type FaqCategory,
  type FaqItem,
} from '../data/faqContent';
import { scrollToTop } from '../utils/scrollUtils';

const CANONICAL = 'https://ablego.co.uk/faq';

function buildFaqJsonLd() {
  const flat = flattenFaqForSchema();
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: flat.map((f) => ({
      '@type': 'Question',
      name: f.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: f.answerText,
      },
    })),
  };
}

function buildBreadcrumbJsonLd() {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'Home',
        item: 'https://ablego.co.uk/',
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: 'FAQ',
        item: CANONICAL,
      },
    ],
  };
}

const FAQPage: React.FC = () => {
  const [query, setQuery] = useState('');

  React.useEffect(() => {
    scrollToTop('instant');
  }, []);

  const normalizedQuery = query.trim().toLowerCase();

  const filteredCategories: FaqCategory[] = useMemo(() => {
    if (!normalizedQuery) return FAQ_CATEGORIES;
    return FAQ_CATEGORIES.map((cat) => ({
      ...cat,
      items: cat.items.filter((item: FaqItem) =>
        allFaqSearchBlob(item, cat.title).includes(normalizedQuery),
      ),
    })).filter((cat) => cat.items.length > 0);
  }, [normalizedQuery]);

  const totalVisible = filteredCategories.reduce((n, c) => n + c.items.length, 0);
  const faqLd = buildFaqJsonLd();
  const crumbLd = buildBreadcrumbJsonLd();
  const jsonSafe = (obj: unknown) =>
    JSON.stringify(obj).replace(/</g, '\\u003c').replace(/\u2028/g, '\\u2028').replace(/\u2029/g, '\\u2029');

  const metaDesc =
    'AbleGo frequently asked questions: accessible UK transport, wheelchair-friendly vehicles, support workers, booking, payments, safeguarding, partnerships, privacy, and organisational bookings.';

  return (
    <>
      <Helmet>
        <title>FAQ — Accessible Transport &amp; Booking Help | AbleGo UK</title>
        <meta name="description" content={metaDesc} />
        <meta
          name="keywords"
          content="AbleGo FAQ, accessible transport UK, wheelchair taxi FAQ, WAV booking help, inclusive transport questions, AbleGo safety, AbleGo bookings"
        />
        <link rel="canonical" href={CANONICAL} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={CANONICAL} />
        <meta
          property="og:title"
          content="FAQ — Accessible Transport Help | AbleGo"
        />
        <meta property="og:description" content={metaDesc} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="AbleGo — Frequently Asked Questions" />
        <meta name="twitter:description" content={metaDesc} />
        <script type="application/ld+json">{jsonSafe(faqLd)}</script>
        <script type="application/ld+json">{jsonSafe(crumbLd)}</script>
      </Helmet>

      <main className="min-h-screen bg-slate-50 dark:bg-dark-900 pt-20 lg:pt-24 pb-20">
        <header className="relative overflow-hidden border-b border-blue-100/80 dark:border-dark-700 bg-gradient-to-br from-blue-600 via-teal-600 to-blue-700 text-white">
          <div
            className="pointer-events-none absolute inset-0 opacity-[0.12]"
            aria-hidden
            style={{
              backgroundImage: `radial-gradient(circle at 20% 20%, white 1px, transparent 1px)`,
              backgroundSize: '28px 28px',
            }}
          />
          <div className="container relative mx-auto px-4 sm:px-6 py-14 lg:py-16 max-w-5xl">
            <div className="flex flex-wrap items-center gap-2 text-sm text-blue-100 mb-4">
              <Link to="/" className="hover:text-white transition-colors">
                Home
              </Link>
              <span aria-hidden>/</span>
              <span className="text-white font-medium">FAQ</span>
            </div>
            <div className="flex items-start gap-3 mb-5">
              <span className="mt-1 inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-white/15 ring-1 ring-white/25">
                <Sparkles className="h-5 w-5" aria-hidden />
              </span>
              <div>
                <h1 className="text-3xl sm:text-4xl lg:text-[2.65rem] font-bold tracking-tight leading-tight">
                  Help hub &amp; frequently asked questions
                </h1>
                <p className="mt-3 max-w-2xl text-blue-50 text-base sm:text-lg leading-relaxed">
                  Clear answers for passengers, families, commissioners, drivers, and support
                  professionals — so you can self-serve confidently, book faster, and know what to
                  expect from inclusive AbleGo journeys.
                </p>
              </div>
            </div>

            <label className="block max-w-xl">
              <span className="sr-only">Search FAQs</span>
              <div className="relative">
                <Search
                  className="pointer-events-none absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-teal-200"
                  aria-hidden
                />
                <input
                  type="search"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search questions (e.g. wheelchair, payment, DBS, schools…)"
                  autoComplete="off"
                  className="w-full rounded-2xl border border-white/25 bg-white/10 py-3.5 pl-12 pr-12 text-white placeholder:text-blue-100/80 backdrop-blur-md focus:outline-none focus:ring-2 focus:ring-white/80"
                />
                {query && (
                  <button
                    type="button"
                    onClick={() => setQuery('')}
                    className="absolute right-3 top-1/2 -translate-y-1/2 rounded-lg px-2 py-1 text-xs font-semibold bg-white/20 hover:bg-white/30"
                  >
                    Clear
                  </button>
                )}
              </div>
            </label>
            <p className="mt-3 text-sm text-blue-100/90">
              {totalVisible} answer{totalVisible !== 1 ? 's' : ''} visible
              {normalizedQuery ? ' (filtered)' : ''} · Updated for UK passengers &amp; partners
            </p>
          </div>
        </header>

        <div className="container mx-auto px-4 sm:px-6 max-w-6xl mt-10 lg:mt-12">
          <div className="lg:grid lg:grid-cols-[minmax(0,260px)_1fr] lg:gap-10 xl:gap-14">
            <aside className="mb-10 lg:mb-0 lg:sticky lg:top-28 self-start">
              <p className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-3">
                Jump to topic
              </p>
              <nav aria-label="FAQ categories">
                <ul className="flex lg:flex-col gap-2 overflow-x-auto pb-1 lg:pb-0 lg:overflow-visible -mx-1 px-1 lg:px-0">
                  {FAQ_CATEGORIES.map((cat) => (
                    <li key={cat.id} className="shrink-0 lg:shrink">
                      <a
                        href={`#${cat.id}`}
                        className="inline-flex lg:w-full items-center gap-2 rounded-xl border border-gray-200 dark:border-dark-700 bg-white dark:bg-dark-800 px-3 py-2 text-sm font-medium text-gray-800 dark:text-gray-100 shadow-sm hover:border-blue-400 hover:bg-blue-50/60 dark:hover:bg-dark-700 transition-colors"
                      >
                        <ChevronDown className="h-4 w-4 shrink-0 -rotate-90 lg:rotate-0 text-teal-600" aria-hidden />
                        <span className="whitespace-nowrap lg:whitespace-normal">{cat.title}</span>
                      </a>
                    </li>
                  ))}
                </ul>
              </nav>

              <div className="mt-8 rounded-2xl bg-gradient-to-br from-blue-600 to-teal-600 text-white p-6 shadow-lg">
                <MessageCircle className="h-8 w-8 opacity-90 mb-3" aria-hidden />
                <p className="font-semibold text-lg mb-2">Still unsure?</p>
                <p className="text-sm text-blue-50 leading-relaxed mb-4">
                  Our team can translate your situation into the right booking or partnership path.
                </p>
                <Link
                  to="/contact"
                  className="inline-flex items-center gap-2 text-sm font-semibold bg-white text-blue-700 rounded-xl px-4 py-2.5 hover:bg-blue-50 transition-colors"
                >
                  Contact support
                  <ArrowRight className="h-4 w-4" aria-hidden />
                </Link>
                <a
                  href="tel:01642089958"
                  className="mt-3 flex items-center gap-2 text-sm text-blue-100 hover:text-white"
                >
                  <Phone className="h-4 w-4 shrink-0" aria-hidden />
                  01642 089 958
                </a>
              </div>
            </aside>

            <div className="min-w-0">
              {!filteredCategories.length && (
                <div className="rounded-2xl border border-amber-200 bg-amber-50 dark:bg-amber-950/30 dark:border-amber-900/60 p-8 text-center">
                  <p className="font-semibold text-amber-950 dark:text-amber-100 mb-2">
                    No matches for “{query}”
                  </p>
                  <p className="text-sm text-amber-900/90 dark:text-amber-200/90 mb-4">
                    Try a shorter word, browse the topics on the left, or reach out — we are happy to
                    help.
                  </p>
                  <button
                    type="button"
                    onClick={() => setQuery('')}
                    className="text-sm font-semibold text-blue-700 dark:text-blue-300 underline"
                  >
                    Show all questions
                  </button>
                </div>
              )}

              <div className="space-y-14">
                {filteredCategories.map((cat) => (
                  <section
                    key={cat.id}
                    id={cat.id}
                    className="scroll-mt-28"
                    aria-labelledby={`heading-${cat.id}`}
                  >
                    <h2
                      id={`heading-${cat.id}`}
                      className="text-2xl font-bold text-gray-900 dark:text-gray-50 mb-2"
                    >
                      {cat.title}
                    </h2>
                    <p className="text-gray-600 dark:text-gray-400 text-sm leading-relaxed mb-6 max-w-3xl">
                      {cat.summary}
                    </p>
                    <ul className="space-y-3 list-none m-0 p-0">
                      {cat.items.map((item: FaqItem) => (
                        <li key={item.id}>
                          <details className="group rounded-2xl border border-gray-200 dark:border-dark-700 bg-white dark:bg-dark-800 shadow-sm hover:shadow-md hover:border-teal-300/60 dark:hover:border-teal-700 transition-[box-shadow,border-color] overflow-hidden">
                            <summary className="flex cursor-pointer list-none items-center justify-between gap-4 px-5 py-4 text-left font-medium text-gray-900 dark:text-gray-50 marker:content-none [&::-webkit-details-marker]:hidden">
                              <span className="pr-2">{item.question}</span>
                              <ChevronDown
                                className="h-5 w-5 shrink-0 text-teal-600 transition-transform group-open:rotate-180"
                                aria-hidden
                              />
                            </summary>
                            <div className="border-t border-gray-100 dark:border-dark-700 px-5 pb-5 pt-0">
                              <div className="pt-4 space-y-3 text-gray-700 dark:text-gray-300 text-sm leading-relaxed">
                                {item.answer.map((paragraph, idx) => (
                                  <p key={idx}>{paragraph}</p>
                                ))}
                              </div>
                            </div>
                          </details>
                        </li>
                      ))}
                    </ul>
                  </section>
                ))}
              </div>

              <section className="mt-16 rounded-2xl bg-white dark:bg-dark-800 border border-gray-200 dark:border-dark-700 p-8 shadow-lg">
                <h2 className="text-xl font-bold text-gray-900 dark:text-gray-50 mb-3">
                  Quick actions
                </h2>
                <p className="text-gray-600 dark:text-gray-400 text-sm mb-6 max-w-2xl">
                  Move from answers to outcomes — whether you need a ride today or long-term inclusive
                  transport for your organisation.
                </p>
                <div className="flex flex-wrap gap-3">
                  <Link
                    to="/booking"
                    className="inline-flex items-center gap-2 rounded-xl bg-blue-600 text-white px-5 py-3 text-sm font-semibold hover:bg-blue-700 transition-colors"
                  >
                    Book a ride
                    <ArrowRight className="h-4 w-4" aria-hidden />
                  </Link>
                  <Link
                    to="/safety"
                    className="inline-flex items-center gap-2 rounded-xl border border-gray-300 dark:border-dark-600 px-5 py-3 text-sm font-semibold text-gray-800 dark:text-gray-100 hover:bg-gray-50 dark:hover:bg-dark-700 transition-colors"
                  >
                    Safety &amp; safeguarding
                  </Link>
                  <Link
                    to="/join"
                    className="inline-flex items-center gap-2 rounded-xl border border-gray-300 dark:border-dark-600 px-5 py-3 text-sm font-semibold text-gray-800 dark:text-gray-100 hover:bg-gray-50 dark:hover:bg-dark-700 transition-colors"
                  >
                    Careers &amp; join us
                  </Link>
                  <Link
                    to="/privacy-policy"
                    className="inline-flex items-center gap-2 rounded-xl border border-gray-300 dark:border-dark-600 px-5 py-3 text-sm font-semibold text-gray-800 dark:text-gray-100 hover:bg-gray-50 dark:hover:bg-dark-700 transition-colors"
                  >
                    Privacy policy
                  </Link>
                </div>
              </section>
            </div>
          </div>
        </div>
      </main>
    </>
  );
};

export default FAQPage;
